import type { StaffHoldoutOptOutConfig } from '../../types/configs.generated';

export type ExperimentVariant = 'control' | 'treatment';

/**
 * Shape returned by `public.get_active_staff_experiment(p_domain)`. Mirrors
 * what the widget needs from a running staff experiment row; the column names
 * match the legacy `staff_holdout` config so downstream code is unchanged.
 *
 * `is_included_for_domain` is TRUE iff p_domain's client is in the
 * experiment's selection AND has no active exclusion. Widget bucketing only
 * runs when this is TRUE.
 */
export interface ActiveStaffExperiment {
    enabled: boolean;
    experiment_id: string | null;
    staff_holdout_pct: number | null;
    start_date: string | null;
    end_date: string | null;
    is_included_for_domain?: boolean | null;
}

/**
 * Shape returned by `public.get_active_domain_experiment(p_domain)`. Mirrors
 * the legacy `client_experiment` config.
 */
export interface ActiveDomainExperiment {
    enabled: boolean;
    experiment_id: string | null;
    start_date: string | null;
    /**
     * IX-3967: exact `experiments.started_at` timestamp (ISO). Stamped into
     * `rw_client_experiment_start_date` when present so the anchor mirror and
     * prior-exposure cutoff compare against the real start moment, not midnight
     * of `start_date`. Falls back to `start_date` for legacy rows.
     */
    start_at?: string | null;
    end_date: string | null;
    control_split_pct: number | null;
    target_mde_pct: number | null;
    confidence: number | null;
    power: number | null;
    is_included_for_domain?: boolean | null;
}

export interface ExperimentCohortProperties {
    rw_staff_experiment_id?: string;
    rw_staff_experiment_start_date?: string;
    rw_staff_experiment_assignment?: ExperimentVariant;
    rw_client_experiment_id?: string;
    rw_client_experiment_start_date?: string;
    rw_client_experiment_assignment?: ExperimentVariant;
    rw_rose_exposure_variant?: ExperimentVariant;
    rw_rose_exposure_reason?: 'staff_holdout' | 'client_experiment' | 'none';
    /**
     * Set when staff manually excluded this domain from a running experiment.
     * Analytics drops these sessions from the eligible cohort so they don't
     * pollute pooled metrics.
     */
    rw_experiment_excluded_reason?: 'staff_experiment' | 'client_experiment';
}

export interface ExperimentCohortDecision {
    widgetAllowed: boolean;
    reason?: string;
    properties: ExperimentCohortProperties;
    staffBucket?: number;
    clientBucket?: number;
}

export interface ExperimentCohortInputs {
    distinctId: string;
    /**
     * The traffic-layer bucket (0–99) for this visitor — the SAME hash the
     * traffic gate uses. The client experiment carve reuses it so treatment,
     * control and excluded are one coherent partition (IX-3984). Falls back to a
     * salted per-experiment hash when absent (direct callers / legacy).
     */
    trafficBucket?: number;
    trafficPercentage?: number | null;
    staffHoldout?: ActiveStaffExperiment | null;
    staffHoldoutOptOut?: StaffHoldoutOptOutConfig | null;
    clientExperiment?: ActiveDomainExperiment | null;
    now?: Date;
}

const STAFF_SLICE_SALT = 'STAFF_SLICE';
const DEFAULT_MAX_BUCKET = 100;

export function hashStringToBucket(value: string, max = DEFAULT_MAX_BUCKET): number {
    let hash = 2166136261;

    for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }

    return Math.abs(hash) % max;
}

export function assignExperimentBucket(distinctId: string, salt: string): number {
    return hashStringToBucket(`${distinctId}${salt}`, DEFAULT_MAX_BUCKET);
}

const CLIENT_CONTROL_SPLIT_MIN = 5;
const CLIENT_CONTROL_SPLIT_MAX = 50;

function clampPercent(value: number | null | undefined): number {
    if (value == null || !Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(100, value));
}

/** Business clamp for the client-experiment control split (share of allocated traffic). */
export function clampClientControlSplitPct(value: number | null | undefined): number {
    return Math.max(CLIENT_CONTROL_SPLIT_MIN, Math.min(CLIENT_CONTROL_SPLIT_MAX, value ?? 10));
}

export interface ClientAllocation {
    /** Width (in 0–100 buckets) of the control span, carved from the leftover. */
    controlBuckets: number;
    /** Width of the treatment span = the visitors who actually see Rose. */
    displayBuckets: number;
    /** display + control; the traffic-layer pass threshold when an experiment runs. */
    ceilingBuckets: number;
}

/**
 * Carve model (IX-3984). `controlSplitPct` is control's share of the A/B test
 * (control / (control + treatment)) — so 50% is a balanced 50/50 test. Control
 * is drawn from the NON-displayed leftover, so display stays equal to the
 * configured traffic % — the client's traffic-allocation number stays truthful.
 *
 *   control  = treatment × split / (100 − split)   (treatment = display)
 *   display  = traffic, shrunk only when control can't fit the leftover
 *   excluded = 100 − display − control
 *
 * Display keeps the balanced ratio when it must shrink (traffic near 100, or
 * high traffic × high split): display = 100 / (1 + r), control = 100 − display,
 * where r = split/(100−split). Single bucket `[0,100)`: [0,display)=treatment,
 * [display,display+control)=control, rest=excluded.
 */
export function computeClientAllocation(
    trafficPercentage: number | null | undefined,
    controlSplitPct: number
): ClientAllocation {
    const traffic = clampPercent(trafficPercentage ?? 100);
    const split = Math.min(clampPercent(controlSplitPct), 99);
    if (split <= 0 || traffic <= 0) {
        return { controlBuckets: 0, displayBuckets: traffic, ceilingBuckets: traffic };
    }
    const r = split / (100 - split);
    let displayBuckets = traffic;
    let controlBuckets = Math.round(traffic * r);
    if (displayBuckets + controlBuckets > 100) {
        // No room to keep full display AND the balanced control — shrink display
        // while preserving the control:treatment ratio so the split stays honest.
        displayBuckets = Math.round(100 / (1 + r));
        controlBuckets = 100 - displayBuckets;
    }
    controlBuckets = Math.max(0, Math.min(100, controlBuckets));
    displayBuckets = Math.max(0, Math.min(displayBuckets, 100 - controlBuckets));
    return {
        controlBuckets,
        displayBuckets,
        ceilingBuckets: displayBuckets + controlBuckets,
    };
}

/**
 * Clamped control split for an ACTIVE, included client experiment, else 0.
 * DisplayController uses this to widen the traffic-layer threshold to the carve
 * ceiling so control-bucket visitors reach cohort resolution and get tagged.
 */
export function activeClientControlSplitPct(
    clientExperiment: ActiveDomainExperiment | null | undefined,
    now: Date = new Date()
): number {
    if (
        clientExperiment?.experiment_id &&
        isActiveWindow(clientExperiment, now) &&
        isIncludedForDomain(clientExperiment)
    ) {
        return clampClientControlSplitPct(clientExperiment.control_split_pct);
    }
    return 0;
}

function parseBoundaryDate(value: string, endOfDay: boolean): Date | null {
    if (!value) return null;

    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
    const parsed = Date.parse(dateOnly ? `${value}T00:00:00.000Z` : value);
    if (Number.isNaN(parsed)) return null;

    const date = new Date(parsed);
    if (dateOnly && endOfDay) {
        date.setUTCHours(23, 59, 59, 999);
    }
    return date;
}

function isActiveWindow(
    config: { enabled?: boolean; start_date?: string | null; end_date?: string | null },
    now: Date
): boolean {
    if (config.enabled !== true) return false;

    const startDate = config.start_date ? parseBoundaryDate(config.start_date, false) : null;
    if (startDate && now < startDate) return false;

    const endDate = config.end_date ? parseBoundaryDate(config.end_date, true) : null;
    if (endDate && now > endDate) return false;

    return true;
}

function isStaffHoldoutEligible(inputs: ExperimentCohortInputs): boolean {
    if ((inputs.trafficPercentage ?? 0) <= 0) return false;
    // Conversion-trackability is decided once, at experiment creation, via the
    // `has_external_tracking` gate in create_staff_experiment_with_selection.
    // The widget must NOT re-derive eligibility per visitor (the legacy v1
    // `form_tracking_pages` check did, and it diverged from the selection gate —
    // blocking CTA-tracked clients like userose.ai that have no form_tracking_pages
    // but a fully-tracked CTA). Selection membership (is_included_for_domain) is
    // the gate; opt_out below is the only live runtime override.
    if (inputs.staffHoldoutOptOut?.opt_out === true) return false;

    return true;
}

function isIncludedForDomain(config: { is_included_for_domain?: boolean | null }): boolean {
    return config.is_included_for_domain !== false;
}

export function resolveExperimentCohortDecision(
    inputs: ExperimentCohortInputs
): ExperimentCohortDecision {
    const now = inputs.now ?? new Date();
    const properties: ExperimentCohortProperties = {};
    let hasActiveExperiment = false;

    // Record per-experiment exclusions for analytics hygiene. Domains that are
    // not in the selection (or that are in an active exclusion row) still load
    // the widget (treatment-equivalent) but downstream analytics drops them
    // from the eligible cohort using this property.
    if (
        inputs.staffHoldout?.experiment_id &&
        isActiveWindow(inputs.staffHoldout, now) &&
        inputs.staffHoldout.is_included_for_domain === false
    ) {
        properties.rw_experiment_excluded_reason = 'staff_experiment';
    } else if (
        inputs.clientExperiment?.experiment_id &&
        isActiveWindow(inputs.clientExperiment, now) &&
        inputs.clientExperiment.is_included_for_domain === false
    ) {
        properties.rw_experiment_excluded_reason = 'client_experiment';
    }

    if (
        inputs.staffHoldout?.experiment_id &&
        isActiveWindow(inputs.staffHoldout, now) &&
        isStaffHoldoutEligible(inputs) &&
        isIncludedForDomain(inputs.staffHoldout)
    ) {
        hasActiveExperiment = true;
        const staffBucket = assignExperimentBucket(inputs.distinctId, STAFF_SLICE_SALT);
        const staffHoldoutPct = Math.max(
            0,
            Math.min(10, inputs.staffHoldout.staff_holdout_pct ?? 5)
        );
        const variantStaff: ExperimentVariant =
            staffBucket < staffHoldoutPct ? 'control' : 'treatment';

        properties.rw_staff_experiment_id = inputs.staffHoldout.experiment_id;
        if (inputs.staffHoldout.start_date) {
            properties.rw_staff_experiment_start_date = inputs.staffHoldout.start_date;
        }
        properties.rw_staff_experiment_assignment = variantStaff;

        if (variantStaff === 'control') {
            properties.rw_rose_exposure_variant = 'control';
            properties.rw_rose_exposure_reason = 'staff_holdout';
            return {
                widgetAllowed: false,
                reason: 'Staff holdout control cohort',
                properties,
                staffBucket,
            };
        }
    }

    if (
        inputs.clientExperiment?.experiment_id &&
        isActiveWindow(inputs.clientExperiment, now) &&
        isIncludedForDomain(inputs.clientExperiment)
    ) {
        hasActiveExperiment = true;
        // Carve model (IX-3984): control is drawn from the non-displayed leftover
        // using the SAME traffic bucket, so display stays = traffic %. Visitors in
        // [0, display) see Rose; those in [display, ceiling) are the tagged control.
        // The resolver only runs once the traffic layer passed (bucket < ceiling),
        // so bucket >= displayBuckets here means control.
        const controlSplitPct = clampClientControlSplitPct(
            inputs.clientExperiment.control_split_pct
        );
        const { displayBuckets } = computeClientAllocation(
            inputs.trafficPercentage,
            controlSplitPct
        );
        const clientBucket =
            inputs.trafficBucket ??
            assignExperimentBucket(inputs.distinctId, inputs.clientExperiment.experiment_id);
        const variantClient: ExperimentVariant =
            clientBucket < displayBuckets ? 'treatment' : 'control';

        properties.rw_client_experiment_id = inputs.clientExperiment.experiment_id;
        // IX-3967: prefer the exact started_at timestamp so the anchor mirror and
        // prior-exposure cutoff use the real start moment, not midnight of the
        // start date. Property key is unchanged (safe_to_timestamptz parses both).
        const clientStart = inputs.clientExperiment.start_at ?? inputs.clientExperiment.start_date;
        if (clientStart) {
            properties.rw_client_experiment_start_date = clientStart;
        }
        properties.rw_client_experiment_assignment = variantClient;

        if (variantClient === 'control') {
            properties.rw_rose_exposure_variant = 'control';
            properties.rw_rose_exposure_reason = 'client_experiment';
            return {
                widgetAllowed: false,
                reason: 'Client experiment control cohort',
                properties,
                clientBucket,
            };
        }

        return {
            widgetAllowed: true,
            properties: {
                ...properties,
                rw_rose_exposure_variant: 'treatment',
                rw_rose_exposure_reason: 'none',
            },
            clientBucket,
        };
    }

    if (hasActiveExperiment) {
        properties.rw_rose_exposure_variant = 'treatment';
        properties.rw_rose_exposure_reason = 'none';
    }

    return {
        widgetAllowed: true,
        properties,
    };
}
